import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { apiClient } from '../api/client';
import {
  useAuthStore,
  type AuthUser,
  getProbeInFlight,
  setProbeInFlight,
} from '../store/authStore';
import { withCrossTabLock, LOCK_AUTH_PROBE } from '../lib/cross-tab-lock';
import { getMySettings } from '../api/users';
import { applyCustomTheme, themeFromSettings } from '../lib/theme-custom';
// 회사 기본 테마(2026-08-20) — 관리 콘솔이 정한 색. 개인 색과 합쳐서 입힌다.
import { applyServerLang, isLang } from '../lib/base-i18n';

/**
 * AuthGuard (auth v1.5 cookie cutover — TD-6).
 *
 * 시나리오:
 *   1. authStatus==='pending' (초기/새로고침 직후) → /users/me probe 1회
 *      - 200      → setSession(user) → authed → children 렌더
 *      - 401      → apiClient interceptor 가 자동 /auth/refresh 1회 시도
 *                     성공 → 재시도 → 200 → setSession
 *                     실패 → interceptor 가 clearSession → anon → /login
 *      - 그 외     → setAuthStatus('anon') (보수적 — 사용자가 /login 으로 회귀)
 *   2. authStatus==='authed' → children 렌더
 *   3. authStatus==='anon'   → /login 으로 from 보존 redirect
 *
 * single-flight (Pre-mortem S3 봉합 — R13):
 *   - 다중 탭 mount + StrictMode 의 effect 2회 실행 시 probe 호출 폭주 차단
 *   - module-level Promise — 첫 호출자만 fetch, 후속 호출자는 같은 promise 를 await
 *
 * 무한루프 가드: interceptor 의 _retry 플래그 + module-level refresh single-flight 가
 * 자동 처리 (api/client.ts).
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const authStatus = useAuthStore((s) => s.authStatus);
  const { pathname } = useLocation();

  useEffect(() => {
    if (authStatus !== 'pending') return;

    // 다른 탭/effect 재진입이 이미 probe 중이면 추가 호출 안 함.
    // StrictMode 의 effect 더블 실행 시 두 번째 effect 가 본 path 로 early return.
    // cancelled flag 는 의도적으로 사용 안 함 — StrictMode 에서 첫 cleanup 이 cancelled=true
    // 로 만들어 setSession 을 차단하면 결과가 store 에 반영되지 않음 (false negative).
    // setSession 은 멱등하므로 두 번 호출돼도 안전.
    if (getProbeInFlight()) return;

    // cross-tab lock 안에서 probe 실행 — 다른 탭이 이미 probe 중이면 본 탭은 lock 대기,
    // lock 획득 시점엔 다른 탭이 BroadcastChannel `auth-probe-result` 로 이미 store
    // update 한 상태라 본 탭은 fetch 생략 (review BLOCK-C 봉합).
    const probe = withCrossTabLock(LOCK_AUTH_PROBE, async () => {
      // lock 진입 시점에 다른 탭이 결과를 broadcast 했는지 재확인.
      if (useAuthStore.getState().authStatus !== 'pending') return;
      try {
        const me = await fetchUserMe();
        // probe success 가 broadcast 의 session-cleared 에 의해 anon 으로 바뀐 후
        // 도착한 경우 stale 갱신 차단 (last-writer-wins 회피, R12 보강).
        if (useAuthStore.getState().authStatus === 'anon') return;
        useAuthStore.getState().setSession(me);
        // i18n 부트 sync — 서버 settings.language 가 계정 언어의 SSOT. 부트마다 적용해
        // navigator.language(영어 브라우저) fallback 또는 stale localStorage 로 UI 가 설정과
        // 다른 언어로 나오던 문제를 봉합. best-effort (실패해도 인증엔 무영향).
        try {
          const s = await getMySettings();
          if (isLang(s.language)) applyServerLang(s.language);
          // 테마 커스터마이즈(2026-08-20) — 고른 색·모서리를 부팅 때 입힌다.
          // 아무것도 안 고른 사람은 값이 전부 null 이라 아무 변수도 덮이지 않는다(= 지금과 같다).
          // ★ 회사 기본 테마는 걷어냈다(2026-09-02) — 테마는 개인이 관리한다.
          applyCustomTheme({ ...themeFromSettings(s), transitionMs: 0 });
        } catch { /* settings sync 는 best-effort — 무시 */ }
      } catch {
        // 실제 인증 실패(401)는 apiClient interceptor 가 이미 clearSession(→ 'anon') 처리했다.
        // 그 경우 authStatus 는 'pending' 이 아니므로 그대로 로그아웃 진행.
        if (useAuthStore.getState().authStatus !== 'pending') return;
        // ── 429(RATE_LIMITED)·네트워크·5xx 등 "미인증이 아닌" 실패는 로그아웃하지 않는다 (2026-07-17 봉합) ──
        // 강력 캐시 지우기 새로고침은 자산+부팅 API 를 버스트로 쏴 글로벌 60/분 rate-limit(server.ts)을
        // 넘길 수 있고, 그러면 /users/me probe 가 429 를 받는다. 429 는 401 이 아니라 "인증은 됐는데
        // 요청이 너무 많다" 이므로 로그아웃은 오처리다. persist 된 최근 세션이 있으면 낙관적으로 유지 —
        // 세션이 실제로 만료됐다면 이후 실제 API 호출이 401 → interceptor 가 정상 로그아웃한다.
        const persistedUser = useAuthStore.getState().user;
        if (persistedUser) {
          useAuthStore.getState().setSession(persistedUser);
        } else {
          useAuthStore.getState().setAuthStatus('anon');
        }
      }
    });

    setProbeInFlight(probe.finally(() => {
      setProbeInFlight(null);
    }));
  }, [authStatus]);

  if (authStatus === 'pending') {
    // probe 진행 중 — 빈 화면 (빠르면 100ms 이내). 별도 spinner UI 는 layout 책임.
    return null;
  }
  if (authStatus === 'anon') {
    return <Navigate to="/login" state={{ from: pathname }} replace />;
  }
  return <>{children}</>;
}

async function fetchUserMe(): Promise<AuthUser> {
  const res = await apiClient.get<{
    user: { id: string; email: string; name: string; role: AuthUser['role']; institutionId: string; avatarUrl?: string | null };
  }>('/users/me');
  return {
    id: Number(res.data.user.id),
    email: res.data.user.email,
    name: res.data.user.name,
    role: res.data.user.role,
    institutionId: Number(res.data.user.institutionId),
    avatarUrl: res.data.user.avatarUrl ?? null,
  };
}
